import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Copy, Check, Clock, Download } from "lucide-react";
import { publicDocsUrl } from "@/lib/docs";


const REQUEST_CURL = `POST /api/v1/ecf HTTP/1.1
Host: api.emite.do
X-API-Key: sk_live_xxxxxxxx
Content-Type: application/json

{
  "idempotency_key": "ord_20260518_001",
  "ecf_type": "31",
  "environment": "eCF",
  "payload": { ... }
}`;

export default function HeroSection() {
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const targetDate = new Date("2026-11-15T00:00:00").getTime();
    const updateCountdown = () => {
      const now = new Date().getTime();
      const difference = targetDate - now;
      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0 });
        return;
      }
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      setTimeLeft({ days, hours, minutes });
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(REQUEST_CURL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="relative min-h-screen bg-slate-950 flex items-center overflow-hidden">
      {/* Grid background */}
      <div className="absolute inset-0 grid-bg opacity-60" />

      {/* Glow */}
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-blue-800/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-blue-900/10 rounded-full blur-2xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 pt-28 pb-20 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left: Text */}
          <div
            className={`flex-1 text-center lg:text-left transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            {/* Countdown Banner */}
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-xl border border-amber-500/30 bg-amber-500/5 mb-6 text-left max-w-lg mx-auto lg:mx-0">
              <Clock className="text-amber-500 flex-shrink-0 animate-pulse" size={16} />
              <div>
                <p className="text-[11px] font-bold text-amber-500 uppercase tracking-wider m-0">Plazo Obligatoriedad DGII (Ley 32-23)</p>
                <p className="text-xs text-slate-300 m-0">
                  Faltan <strong className="text-white font-mono">{timeLeft.days}</strong> días, <strong className="text-white font-mono">{timeLeft.hours}</strong> horas para el cumplimiento obligatorio de Micro y Pequeñas empresas.
                </p>
              </div>
            </div>

            {/* Badge */}
            <div className="block lg:flex items-center gap-2 mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-blue-700/40 bg-blue-900/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-slow" />
                <span className="text-xs font-medium text-blue-300">Certificado DGII · Producción 24/7</span>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
              Tu ERP y tu facturación electrónica,{" "}
              <span className="text-blue-400">en una sola plataforma</span>
            </h1>

            <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              Cumple con la Ley 32-23 antes del 15 de noviembre — y de paso deja de llevar el inventario en Excel.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start">
              <Link
                to="/signup"
                className="flex items-center gap-2 bg-blue-700 hover:bg-blue-600 text-white font-semibold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                Registrar empresa
                <ArrowRight size={16} />
              </Link>
              <a
                href={publicDocsUrl()}
                className="flex items-center gap-2 text-slate-300 hover:text-white font-medium px-4 py-3 transition-colors cursor-pointer whitespace-nowrap"
              >
                <BookOpen size={16} />
                Ver documentación
              </a>
            </div>

            {/* Asset downloads block */}
            <div className="mt-6 flex flex-wrap gap-4 justify-center lg:justify-start border-t border-slate-900 pt-6">
              <a
                href="#postman"
                onClick={(e) => {
                  e.preventDefault();
                  alert("Colección de Postman descargada: eCF_Service_Postman_collection.json");
                }}
                className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                <Download size={13} />
                Colección Postman
              </a>
            </div>

            {/* Compat band */}
            <div className="mt-10 flex flex-wrap items-center gap-4 justify-center lg:justify-start">
              <span className="text-xs text-slate-500 uppercase tracking-widest">Compatible con</span>
              {["Odoo", "SAP", "Dynamics", "Oracle", "NetSuite"].map((erp) => (
                <span
                  key={erp}
                  className="text-sm font-semibold text-slate-400 border border-slate-700 rounded-full px-3 py-1"
                >
                  {erp}
                </span>
              ))}
            </div>
          </div>

          {/* Right: API card */}
          <div
            className={`flex-1 w-full max-w-lg lg:max-w-none transition-all duration-700 delay-300 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <div className="animate-float">
              <div className="bg-slate-900/80 border border-slate-700/60 rounded-2xl overflow-hidden shadow-2xl backdrop-blur-sm">
                {/* Card header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/60" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                    <div className="w-3 h-3 rounded-full bg-green-500/60" />
                  </div>
                  <span className="text-xs text-slate-500 font-mono">api.emite.do</span>
                  <button onClick={handleCopy} className="text-slate-500 hover:text-white transition-colors cursor-pointer">
                    {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                  </button>
                </div>

                {/* Request */}
                <div className="px-4 pt-4 pb-2">
                  <p className="text-xs text-slate-500 mb-2 font-mono uppercase tracking-widest">Request</p>
                  <pre className="text-xs font-mono leading-relaxed overflow-x-auto">
                    <code>
                      <span className="text-blue-400">POST</span>
                      <span className="text-slate-300"> /api/v1/ecf HTTP/1.1</span>{"\n"}
                      <span className="text-slate-500">Host: </span>
                      <span className="text-slate-300">api.emite.do</span>{"\n"}
                      <span className="text-slate-500">X-API-Key: </span>
                      <span className="text-yellow-300">sk_live_xxxxxxxx</span>{"\n"}
                      <span className="text-slate-500">Content-Type: </span>
                      <span className="text-slate-300">application/json</span>{"\n\n"}
                      <span className="text-slate-400">{"{"}</span>{"\n"}
                      <span className="text-slate-400">{"  "}</span>
                      <span className="text-emerald-300">&quot;idempotency_key&quot;</span>
                      <span className="text-slate-400">: </span>
                      <span className="text-orange-300">&quot;ord_20260518_001&quot;</span>
                      <span className="text-slate-400">,</span>{"\n"}
                      <span className="text-slate-400">{"  "}</span>
                      <span className="text-emerald-300">&quot;ecf_type&quot;</span>
                      <span className="text-slate-400">: </span>
                      <span className="text-orange-300">&quot;31&quot;</span>
                      <span className="text-slate-400">,</span>{"\n"}
                      <span className="text-slate-400">{"  "}</span>
                      <span className="text-emerald-300">&quot;environment&quot;</span>
                      <span className="text-slate-400">: </span>
                      <span className="text-orange-300">&quot;eCF&quot;</span>{"\n"}
                      <span className="text-slate-400">{"}"}</span>
                    </code>
                  </pre>
                </div>

                {/* Divider */}
                <div className="mx-4 my-2 border-t border-slate-800" />

                {/* Response */}
                <div className="px-4 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-slate-500 font-mono uppercase tracking-widest">Response</span>
                    <span className="text-xs bg-emerald-900/40 text-emerald-400 border border-emerald-700/40 px-2 py-0.5 rounded-full font-mono">200 OK</span>
                  </div>
                  <pre className="text-xs font-mono leading-relaxed overflow-x-auto">
                    <code>
                      <span className="text-slate-400">{"{"}</span>{"\n"}
                      <span className="text-slate-400">{"  "}</span>
                      <span className="text-emerald-300">&quot;status&quot;</span>
                      <span className="text-slate-400">: </span>
                      <span className="text-emerald-400 font-semibold">&quot;Aceptado&quot;</span>
                      <span className="text-slate-400">,</span>{"\n"}
                      <span className="text-slate-400">{"  "}</span>
                      <span className="text-emerald-300">&quot;track_id&quot;</span>
                      <span className="text-slate-400">: </span>
                      <span className="text-orange-300">&quot;ECF-20260518-00142&quot;</span>
                      <span className="text-slate-400">,</span>{"\n"}
                      <span className="text-slate-400">{"  "}</span>
                      <span className="text-emerald-300">&quot;security_code&quot;</span>
                      <span className="text-slate-400">: </span>
                      <span className="text-yellow-300">&quot;85F2A1&quot;</span>
                      <span className="text-slate-400">,</span>{"\n"}
                      <span className="text-slate-400">{"  "}</span>
                      <span className="text-emerald-300">&quot;ecf_type&quot;</span>
                      <span className="text-slate-400">: </span>
                      <span className="text-orange-300">&quot;31&quot;</span>
                      <span className="text-slate-400">,</span>{"\n"}
                      <span className="text-slate-400">{"  "}</span>
                      <span className="text-emerald-300">&quot;timestamp&quot;</span>
                      <span className="text-slate-400">: </span>
                      <span className="text-orange-300">&quot;2026-05-18T14:22:31Z&quot;</span>{"\n"}
                      <span className="text-slate-400">{"}"}</span>
                    </code>
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </section>
  );
}